import { apiFetch } from './client';
import { parseJsonResponse, type PaginationMeta } from './parseResponse';

export type RecruitmentJobStatus = 'draft' | 'published' | 'closed' | 'archived';
export type ApplicationStatus =
  | 'new'
  | 'screening'
  | 'shortlisted'
  | 'interview'
  | 'final_review'
  | 'accepted'
  | 'rejected';

export type RecruitmentJob = {
  id: number;
  title: string;
  slug: string;
  department: string;
  employment_type: string;
  location: string;
  description?: string | null;
  responsibilities: string[];
  requirements: string[];
  nice_to_have: string[];
  benefits: string[];
  skills: string[];
  status: RecruitmentJobStatus;
  published_at?: string | null;
  applications_count?: number;
  created_at?: string | null;
};

export type RecruitmentApplication = {
  id: number;
  application_number: string;
  full_name: string;
  email: string;
  phone?: string | null;
  city?: string | null;
  years_experience?: number | null;
  specialty?: string | null;
  status: ApplicationStatus;
  job?: { id: number; title: string; slug: string; department: string } | null;
  has_cv: boolean;
  created_at?: string | null;
  linkedin_url?: string | null;
  portfolio_url?: string | null;
  bio?: string | null;
  cv_original_name?: string | null;
  cv_mime?: string | null;
  cv_size?: number | null;
  portfolio_file_name?: string | null;
  consent?: boolean;
  notes?: { id: number; body: string; author?: string | null; created_at?: string | null }[];
  timeline?: {
    id: number;
    type: string;
    label: string;
    from_status?: string | null;
    to_status?: string | null;
    actor?: string | null;
    created_at?: string | null;
  }[];
};

export type RecruitmentSummary = {
  total: number;
  new: number;
  screening: number;
  shortlisted: number;
  interview: number;
  final_review: number;
  accepted: number;
  rejected: number;
};

export type RecruitmentSettings = {
  notify_email?: string | null;
  accepting_applications: boolean;
  honeypot_enabled: boolean;
  cv_max_kilobytes: number;
};

export type JobPayload = {
  title: string;
  slug?: string;
  department: string;
  employment_type: string;
  location: string;
  description?: string;
  responsibilities?: string[];
  requirements?: string[];
  nice_to_have?: string[];
  benefits?: string[];
  skills?: string[];
  status?: RecruitmentJobStatus;
};

type Fail = { ok: false; message: string; unauthorized?: boolean };

async function unwrap<T>(res: Response): Promise<{ ok: true; data: T; meta?: PaginationMeta } | Fail> {
  const out = await parseJsonResponse<T>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true, data: out.data as T, meta: out.meta };
}

export async function fetchRecruitmentJobs(params?: {
  page?: number;
  search?: string;
  status?: string;
}): Promise<{ ok: true; data: RecruitmentJob[]; meta?: PaginationMeta } | Fail> {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.search) q.set('search', params.search);
  if (params?.status) q.set('status', params.status);
  q.set('per_page', '50');
  const res = await apiFetch(`/platform/recruitment/jobs?${q.toString()}`);
  return unwrap<RecruitmentJob[]>(res);
}

export async function fetchRecruitmentJob(id: number): Promise<{ ok: true; data: RecruitmentJob } | Fail> {
  const res = await apiFetch(`/platform/recruitment/jobs/${id}`);
  return unwrap<RecruitmentJob>(res);
}

export async function createRecruitmentJob(payload: JobPayload): Promise<{ ok: true; data: RecruitmentJob } | Fail> {
  const res = await apiFetch('/platform/recruitment/jobs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return unwrap<RecruitmentJob>(res);
}

export async function updateRecruitmentJob(
  id: number,
  payload: JobPayload,
): Promise<{ ok: true; data: RecruitmentJob } | Fail> {
  const res = await apiFetch(`/platform/recruitment/jobs/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return unwrap<RecruitmentJob>(res);
}

export async function deleteRecruitmentJob(id: number): Promise<{ ok: true } | Fail> {
  const res = await apiFetch(`/platform/recruitment/jobs/${id}`, { method: 'DELETE' });
  const out = await unwrap<null>(res);
  if (out.ok === false) return out;
  return { ok: true };
}

export async function setRecruitmentJobAction(
  id: number,
  action: 'publish' | 'hide' | 'close' | 'archive',
): Promise<{ ok: true; data: RecruitmentJob } | Fail> {
  const res = await apiFetch(`/platform/recruitment/jobs/${id}/${action}`, { method: 'POST' });
  return unwrap<RecruitmentJob>(res);
}

export async function fetchRecruitmentSummary(): Promise<{ ok: true; data: RecruitmentSummary } | Fail> {
  const res = await apiFetch('/platform/recruitment/applications/summary');
  return unwrap<RecruitmentSummary>(res);
}

export async function fetchRecruitmentApplications(params?: {
  page?: number;
  search?: string;
  status?: string;
  job_id?: number;
}): Promise<{ ok: true; data: RecruitmentApplication[]; meta?: PaginationMeta } | Fail> {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.search) q.set('search', params.search);
  if (params?.status) q.set('status', params.status);
  if (params?.job_id) q.set('job_id', String(params.job_id));
  q.set('per_page', '30');
  const res = await apiFetch(`/platform/recruitment/applications?${q.toString()}`);
  return unwrap<RecruitmentApplication[]>(res);
}

export async function fetchRecruitmentApplication(
  id: number,
): Promise<{ ok: true; data: RecruitmentApplication } | Fail> {
  const res = await apiFetch(`/platform/recruitment/applications/${id}`);
  return unwrap<RecruitmentApplication>(res);
}

export async function patchApplicationStatus(
  id: number,
  status: ApplicationStatus,
): Promise<{ ok: true; data: RecruitmentApplication } | Fail> {
  const res = await apiFetch(`/platform/recruitment/applications/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  return unwrap<RecruitmentApplication>(res);
}

export async function addApplicationNote(
  id: number,
  body: string,
): Promise<{ ok: true; data: RecruitmentApplication } | Fail> {
  const res = await apiFetch(`/platform/recruitment/applications/${id}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  return unwrap<RecruitmentApplication>(res);
}

export async function openApplicationCv(id: number, mode: 'view' | 'download'): Promise<{ ok: true } | Fail> {
  const res = await apiFetch(
    `/platform/recruitment/applications/${id}/cv${mode === 'view' ? '?inline=1' : ''}`,
  );
  if (res.status === 401) return { ok: false, message: 'Unauthorized', unauthorized: true };
  if (!res.ok) return { ok: false, message: `HTTP ${res.status}` };
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  if (mode === 'view') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = `cv-${id}`;
    a.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { ok: true };
}

export async function fetchRecruitmentSettings(): Promise<{ ok: true; data: RecruitmentSettings } | Fail> {
  const res = await apiFetch('/platform/recruitment/settings');
  return unwrap<RecruitmentSettings>(res);
}

export async function saveRecruitmentSettings(
  payload: Partial<RecruitmentSettings>,
): Promise<{ ok: true; data: RecruitmentSettings } | Fail> {
  const res = await apiFetch('/platform/recruitment/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return unwrap<RecruitmentSettings>(res);
}
